import {Injectable} from '@angular/core';
import {
  deserialize,
  JsonApiParams,
  JsonApiResponse,
  objectToHttpParams,
  serialize,
} from '@tisa-group/cdk';
import {Observable} from 'rxjs';
import {
  UmpireQuiz,
  UmpireQuizAnswer,
  UmpireQuizQuestion,
  UmpireQuizRanking,
  UmpireQuizUserAnswer,
  UmpireQuizUserAttempt,
} from '@tisa-group/models';
import {TisaUmpireHttpService} from '../tisa-umpire-http.service';

export interface QuizUserAnswerPayload {
  type: 'quiz_user_answer';
  answer_time: number;
  quiz: Partial<UmpireQuiz>;
  quiz_answer: Partial<UmpireQuizAnswer> | null;
  quiz_question: Partial<UmpireQuizQuestion>;
  relationshipNames: string[];
}

@Injectable({
  providedIn: 'root',
})
export class UmpireClientQuizService {
  constructor(private readonly _umpireHttpService: TisaUmpireHttpService) {}

  fetch(id: string, params?: JsonApiParams): Observable<JsonApiResponse<UmpireQuiz>> {
    return this._umpireHttpService
      .get(`/quizzes/${id}`, {
        params: objectToHttpParams(params),
      })
      .pipe(deserialize());
  }

  list(params?: JsonApiParams): Observable<JsonApiResponse<UmpireQuiz[]>> {
    return this._umpireHttpService
      .get(`/quizzes`, {
        params: objectToHttpParams(params),
      })
      .pipe(deserialize());
  }

  nextQuestion(
    quizId: string,
    params?: JsonApiParams,
  ): Observable<JsonApiResponse<UmpireQuizQuestion>> {
    return this._umpireHttpService
      .get(`/quiz_questions/next_question?quiz_id=${quizId}`, {
        params: objectToHttpParams(params),
      })
      .pipe(deserialize());
  }

  saveUserAnswer(
    body: QuizUserAnswerPayload,
  ): Observable<JsonApiResponse<UmpireQuizUserAnswer>> {
    return this._umpireHttpService
      .post('/quiz_user_answers', serialize({stuff: body}), {})
      .pipe(deserialize());
  }

  finishQuiz(
    quizId: string,
    params?: JsonApiParams,
  ): Observable<JsonApiResponse<UmpireQuiz>> {
    return this._umpireHttpService
      .patch(`/quizzes/${quizId}/finish_quiz`, {}, {params: objectToHttpParams(params)})
      .pipe(deserialize());
  }

  quizRanking(
    quizRankingId: string,
    params?: JsonApiParams,
  ): Observable<JsonApiResponse<UmpireQuizRanking>> {
    return this._umpireHttpService
      .get(`/quiz_rankings/${quizRankingId}`, {
        params: objectToHttpParams(params),
      })
      .pipe(deserialize());
  }

  listQuizRankings(
    params?: JsonApiParams,
  ): Observable<JsonApiResponse<UmpireQuizRanking[]>> {
    return this._umpireHttpService
      .get('/quiz_rankings', {
        params: objectToHttpParams(params),
      })
      .pipe(deserialize());
  }

  quizRankingUserAttempts(
    quizRankingId: string,
    params?: JsonApiParams,
  ): Observable<JsonApiResponse<UmpireQuizUserAttempt[]>> {
    return this._umpireHttpService
      .get(`/quiz_rankings/${quizRankingId}/quiz_user_attempts`, {
        params: objectToHttpParams(params),
      })
      .pipe(deserialize());
  }
}
